import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, ZoomControl } from "react-leaflet";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import { useContext } from "react";
import { MapContext } from "../../Context/OptionsApi";
import { v4 as uuidv4 } from "uuid";
import { useMap } from "react-leaflet/hooks";
import { getWeekDates } from "../../helper/states";
import L from "leaflet";

import {
  districts_style,
  india_style,
  state_style_pre,
  state_style_curr,
  state_style_next,
} from "../../helper/map_styles";
import Loader from "./Loader";

const weeks = getWeekDates();

const DEFAULT_CENTER = [20.5937, 78.9629];

const getCentroid = (coordinates) => {
  if (!coordinates || !Array.isArray(coordinates)) return null;

  let x = 0, y = 0, count = 0;

  const flatten = (coords) => {
    if (!Array.isArray(coords)) return;
    coords.forEach((ring) => {
      if (Array.isArray(ring[0])) {
        flatten(ring);
      } else if (
        Array.isArray(ring) &&
        ring.length >= 2 &&
        typeof ring[0] === "number" &&
        typeof ring[1] === "number"
      ) {
        x += ring[0];
        y += ring[1];
        count++;
      }
    });
  };

  try {
    flatten(coordinates);
    return count > 0 ? [y / count, x / count] : null;
  } catch (err) {
    console.warn("Invalid coordinates in getCentroid:", coordinates);
    return null;
  }
};

function Childleaflet({ geojsonData, filters }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const getZoomLevel = () => {
      if (filters.sub_district && filters.villages?.village) return 13;
      if (filters.sub_district) return 10;
      if (filters.district) return 9;
      if (filters.state) return 7;
      return 4;
    };

    const zoom = getZoomLevel();
    let center = [filters.lat ?? DEFAULT_CENTER[0], filters.lon ?? DEFAULT_CENTER[1]];

    if (geojsonData?.features?.length > 0) {
      const geom = geojsonData.features[0].geometry;
      let centroid = null;

      if (geom?.type === "Polygon" && geom.coordinates?.[0]?.length > 0) {
        centroid = getCentroid(geom.coordinates);
      } else if (geom?.type === "MultiPolygon" && geom.coordinates?.[0]?.[0]?.length > 0) {
        centroid = getCentroid(geom.coordinates[0]);
      }

      if (centroid && centroid.every(c => typeof c === 'number' && !isNaN(c))) {
        center = centroid;
      }
    }

    map.setView(center, zoom, { animate: true });
  }, [geojsonData, filters, map]);

  return null;
}

export default function Maps() {
  const { filters, setFilters, sowingDate, setSowingDate, setGeojsonData } = useContext(MapContext);

  const [originalGeojson, setOriginalGeojson] = useState(null);
  const [displayGeojson, setDisplayGeojson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [noDataMessage, setNoDataMessage] = useState("");
  const mapRef = useRef();
  const [geojsonKey, setGeojsonKey] = useState(uuidv4());

  const safeLat = filters.lat ?? DEFAULT_CENTER[0];
  const safeLon = filters.lon ?? DEFAULT_CENTER[1];
  const baseURL2 = process.env.REACT_APP_BASE_URL_WEB;

  async function getGeoJson() {
    try {
      setLoading(true);
      const selectedInfest = filters.infest_options?.find(
        i => i.id === filters.infestation_id
      );
      const infestationName = selectedInfest?.name || "";
      const url =
        filters.state && filters.district && filters.sub_district
          ? `${baseURL2}/api/location/sub_distgeojson?state=${filters.state.state_id}&district=${filters.district.district}&sub_district=${filters.sub_district.sub_district}&crop=${filters.crop ?? ""}&infest=${infestationName}`
          : filters.state && filters.district
            ? `${baseURL2}/api/location/geojson?state=${filters.state.state_id}&district=${filters.district.district}&crop=${filters.crop ?? ""}&infest=${infestationName}`
            : filters.state
              ? `${baseURL2}/api/location/states_geojson?state=${filters.state.state_id}`
              : `${baseURL2}/api/location/ind_states`;

      const response = await axios.get(url);

      if (!response.data || !response.data.geojson || !response.data.geojson.features.length) {
        setNoDataMessage("No map data available for the selected filters!");
        setNoData(true);
        setOriginalGeojson(null);
        setDisplayGeojson(null);
        setSowingDate(null);
        return;
      }
      const { geojson } = response.data;
      setOriginalGeojson(geojson);
      setGeojsonKey(uuidv4());
      setGeojsonData(geojson);

      if (filters.sub_district && geojson.features) {
        const villageOptions = geojson.features
          .filter(f => f.properties?.village)
          .map(f => ({ village: f.properties.village }));

        setFilters(prev => {
          const currentVillage = prev.villages?.village ?? null;
          const stillValid = currentVillage
            ? villageOptions.some(v => v.village === currentVillage)
            : false;
          return {
            ...prev,
            villages_options: villageOptions,
            villages: stillValid ? prev.villages : null,
          };
        });
      } else {
        setFilters(prev => ({ ...prev, villages_options: [], villages: null }));
      }

      if (filters.district?.district_id) {
        setSowingDate(geojson?.features[0]?.properties?.date || null);
      } else {
        setSowingDate(null);
      }
    } catch (error) {
      console.error("Error fetching GeoJSON data:", error.message);
      setNoDataMessage("No Infestation data available!");
      setNoData(true);
      setOriginalGeojson(null);
      setDisplayGeojson(null);
      setSowingDate(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!originalGeojson) {
      setDisplayGeojson(null);
      return;
    }

    const selectedVillage = filters.villages?.village;
    let filtered = originalGeojson;

    if (selectedVillage && filters.sub_district) {
      const filteredFeatures = originalGeojson.features.filter(
        f => f.properties?.village === selectedVillage
      );
      if (!filteredFeatures.length) {
        setNoDataMessage("No features found for the selected village!");
        setNoData(true);
      }

      filtered = {
        type: "FeatureCollection",
        features: filteredFeatures.map(f => ({
          ...f,
          properties: { ...f.properties },
          geometry: f.geometry ? JSON.parse(JSON.stringify(f.geometry)) : null,
        })),
      };
    } else if (originalGeojson?.features) {
      filtered = {
        type: "FeatureCollection",
        features: originalGeojson.features.map(f => ({
          ...f,
          properties: { ...f.properties },
          geometry: f.geometry ? JSON.parse(JSON.stringify(f.geometry)) : null,
        })),
      };
    }
    setDisplayGeojson(filtered);
    setGeojsonKey(uuidv4());
  }, [filters.villages?.village, originalGeojson, filters?.sub_district]);


  useEffect(() => {
    getGeoJson();
  }, [filters.state, filters.district, filters.sub_district, filters.crop, filters.infest]);
  function onEachFeature(feature, layer) {
    if (feature?.properties && filters?.district?.district_id) {
      layer.on({
        mouseover: (e) => {
          const popupContent = `<b>Village:</b> ${feature.properties.village || 'Unknown'}`;
          e.target.bindPopup(popupContent).openPopup();
          e.target.setStyle({ weight: 1 });
        },
        mouseout: (e) => {
          e.target.closePopup();
          e.target.setStyle({ weight: 0.5 });
        },
        click: (e) => {
          const popupContent = `<b>Village:</b> ${feature.properties.village || 'Unknown'}`;
          e.target.bindPopup(popupContent).openPopup();
        },
      });
    }
  }
  const getStyle = (index) => {
    if (!filters.state) return india_style;
    if (filters.state && !filters.district?.district_id) return districts_style;
    return index === 0 ? state_style_pre : index === 1 ? state_style_curr : state_style_next;
  };

  const getZoomLevel = () => {
    if (filters.sub_district && filters.villages?.village) return 13;
    if (filters.sub_district) return 10;
    if (filters.district) return 9;
    if (filters.state) return 7;
    return 4;
  };
  return (
    <>
      <div className="flex grow mx-5 justify-between relative">
        {[
          { label: "Previous Week", week: weeks.previousWeek },
          { label: "Current Week", week: weeks.currentWeek },
          { label: "Next Week", week: weeks.nextWeek },
        ].map(({ label, week }, index) => (
          <div
            key={label}
            className="w-[33%] relative border-2 border-gray-400 rounded-md flex flex-col justify-between"
          >
            <p
              style={{ zIndex: 1000 }}
              className="w-full absolute text-center bg-gray-200 text-xs font-bold py-0.5"
            >
              {label} ({week})
            </p>
            <MapContainer
              center={[safeLat, safeLon]}
              zoom={getZoomLevel()}
              style={{ height: "100%", width: "100%" }}
              zoomControl={false}
              whenReady={(mapInstance) => {
                mapRef.current = mapInstance;
              }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {displayGeojson && (
                <GeoJSON
                  key={`${index}-${geojsonKey}`}
                  data={displayGeojson}
                  style={getStyle(index)}
                  onEachFeature={onEachFeature}
                />
              )}
              <ZoomControl position="topright" />
              <Childleaflet geojsonData={displayGeojson} filters={filters} />
            </MapContainer>
          </div>
        ))}
        {loading && (
          <div
            style={{ zIndex: 1000 }}
            className="w-full h-full absolute flex justify-around items-center bg-white bg-opacity-70"
          >
            <Loader />
            <Loader />
            <Loader />
          </div>
        )}
      </div>
      {noData && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black bg-opacity-30">
          <div className="bg-white p-5 rounded shadow-lg w-96 text-center z-[10000]">
            <p className="text-gray-700">{noDataMessage}</p>
            <button
              className="mt-3 bg-blue-600 text-white px-4 py-2 rounded"
              onClick={() => setNoData(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}